import React, { useState, useEffect } from 'react';
import MainLayout from '../layouts/MainLayout';
import { useLocation } from 'react-router-dom';

// Dynamically import all images from specific folders
const importAll = (r) => r.keys().map((key) => r(key).default || r(key));

const anualDayImages = importAll(require.context('../assets/img/anual-day', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const holiImages = importAll(require.context('../assets/img/holi', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const bhajanImages = importAll(require.context('../assets/img/bhajan', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const bhoomiPujanImages = importAll(require.context('../assets/img/bhoomi-pujan', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const birthdayImages = importAll(require.context('../assets/img/birthday', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const buildingImages = importAll(require.context('../assets/img/building', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const independenceDayImages = importAll(require.context('../assets/img/independence-republic-day', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const ourTeamImages = importAll(require.context('../assets/img/our-team', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const teejImages = importAll(require.context('../assets/img/teej', false, /\.(png|jpe?g|avif|webp|svg)$/i));
const galleryCategories = [
    { id: 'holi', title: "Holi Milan Samaroh", images: holiImages },
    { id: 'bhajan', title: "Bhajan Sandhya", images: bhajanImages },
    { id: 'annual-day', title: "Annual Day Celebrations", images: anualDayImages },
    { id: 'bhoomi-pujan', title: "Bhoomi Pujan", images: bhoomiPujanImages },
    { id: 'birthday', title: "Birthday Celebrations", images: birthdayImages },
    { id: 'building', title: "Building Construction", images: buildingImages },
    { id: 'our-team', title: "Our Team", images: ourTeamImages },    
    { id: 'teej', title: "Teej Celebration", images: teejImages },
    { id: 'independence-republic-day', title: "Independence & Republic Day", images: independenceDayImages }
];

function Gallery() {
    const { hash, pathname } = useLocation();
    const [activeFilter, setActiveFilter] = useState('All');
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        window.scrollTo(0, 0); // Always scroll to top when opening the page
    }, [pathname]);

    useEffect(() => {
        if (hash) {
            const hashValue = hash.substring(1); // remove '#'
            const validCategory = galleryCategories.find(c => c.id === hashValue);
            if (validCategory) {
                setActiveFilter(hashValue);
            } else {
                setActiveFilter('All');
            }
        } else {
            setActiveFilter('All');
        }
    }, [hash]);

    const filteredCategories = activeFilter === 'All' 
        ? galleryCategories 
        : galleryCategories.filter(cat => cat.id === activeFilter);

    return (
        <MainLayout>
            <section className="gallery-section bg-light py-5 min-vh-100">
                <div className="container section-title text-center mb-5 mt-4" data-aos="fade-up">
                    <h1 className="display-4 fw-bolder mb-3 text-uppercase text-heritage-maroon">
                        Photo Gallery
                    </h1>
                    <div className="mx-auto mb-4" style={{ width: '80px', height: '4px', background: 'linear-gradient(to right, #8c1c13, #0d1b2a)', borderRadius: '2px' }}></div>
                    <p className="text-muted fs-5">Explore the moments and memories of Mahawar Sabha, Rewari.</p>
                </div>

                {/* Category Filter Buttons */}
                <div className="container mb-5 text-center" data-aos="fade-up" data-aos-delay="50">
                    <div className="d-flex justify-content-center flex-wrap gap-2">
                        <button 
                            onClick={() => setActiveFilter('All')} 
                            className={`btn rounded-pill px-4 fw-medium ${activeFilter === 'All' ? 'btn-danger shadow' : 'btn-outline-danger'}`}
                        >
                            All
                        </button>
                        {galleryCategories.map((category) => (
                            <button 
                                key={category.id}
                                onClick={() => setActiveFilter(category.id)} 
                                className={`btn rounded-pill px-4 fw-medium ${activeFilter === category.id ? 'btn-danger shadow' : 'btn-outline-danger'}`}
                            >
                                {category.title}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="container" data-aos="fade-up" data-aos-delay="100">
                    {filteredCategories.map((category, index) => (
                        category.images.length > 0 && (
                            <div key={index} className="mb-5 bg-white p-4 rounded-4 shadow-sm border-gold">
                                <h3 className="fw-bold text-dark mb-4 border-bottom pb-2 d-inline-block">
                                    {category.title}
                                </h3>
                                <div className="row g-4">
                                    {category.images.map((img, imgIndex) => (
                                        <div className="col-sm-6 col-md-4 col-lg-3" key={imgIndex}>
                                            <div className="img-wrapper rounded-3 shadow-sm overflow-hidden hover-lift h-100 bg-light p-1 border">
                                                <img 
                                                    src={img} 
                                                    alt={`${category.title} ${imgIndex + 1}`} 
                                                    className="img-fluid w-100 rounded-2" 
                                                    style={{ height: '220px', objectFit: 'contain', cursor: 'pointer' }} 
                                                    loading="lazy"
                                                    onClick={() => setSelectedImage(img)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    ))}
                </div>

                {/* Lightbox Modal */}
                {selectedImage && (
                    <div 
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
                        style={{ backgroundColor: 'rgba(0,0,0,0.9)', zIndex: 9999 }}
                        onClick={() => setSelectedImage(null)}
                    >
                        <button 
                            className="btn-close btn-close-white position-absolute top-0 end-0 m-4 p-2" 
                            onClick={() => setSelectedImage(null)}
                        ></button>
                        <img 
                            src={selectedImage} 
                            alt="Full Screen View" 
                            style={{ maxHeight: '90vh', maxWidth: '90vw', objectFit: 'contain' }} 
                        />
                    </div>
                )}
            </section>
        </MainLayout>
    );
}

export default Gallery;